/**
 * The map panel (D34): the discovered world, drawn.
 *
 * @remarks
 * Pure and prop-driven like every component here. It draws the engine's
 * POSITIONED PRIMITIVES (`MapView['layout']`) as native JSX — so there is no
 * `dangerouslySetInnerHTML`, no HTML parsing, and no way for content to
 * become markup. It does NOT compute layout: `deriveMapGraph` and the two
 * layouts are the single source of truth, and a UI that re-derived either
 * would be exactly the parallel surface D32 deleted. D169: the layout says
 * its `style`, and the panel draws the flat grid or the hologram by it —
 * the same section, heading and annotations around either. D210: the scene
 * is drawn by a renderer this package never imports (three.js is
 * `player-scene`'s, loaded by the shell on first use), so the panel takes
 * it as a render slot and keeps the heading, the legend, the text-map hint
 * and the annotations around it like any other style.
 *
 * Accessibility: the figure is `role="img"` with a title and the engine's own
 * description; room names are real `<text>` nodes; and the annotation list
 * beneath it is ordinary DOM text, so the ways this map cannot draw stay
 * readable by everyone. Knowledge tiers differ by stroke style as well as
 * color.
 *
 * @packageDocumentation
 */

import { type MapGraph } from '@orpheus/api-contracts';
import { type MapLayout } from '@orpheus/engine-runtime';
import { type ReactNode } from 'react';

import { BasicMapFigure } from './basic-map-figure.js';
import { HoloMapFigure, HoloMapLegend } from './holo-map-figure.js';

/** The scene's layout — the engine's boxes in a y-up world, tagged `scene`, with the pack's tint (D210). */
export type SceneMapLayout = Extract<MapLayout, { readonly style: 'scene' }>;

/** The one line every style keeps in reach of a screen reader: the text map is a directive away. */
export const TEXT_MAP_HINT = 'Read it as text: MAP';

/** D210: what the panel says when the player chose the scene and this browser cannot draw one. */
export const SCENE_STAND_IN_NOTICE = 'This browser cannot draw the 3D map, so the hologram stands in.';

/** Props for {@link MapPanelComponent}. */
export interface MapPanelComponentProps {
	/** Links the grid cannot draw honestly (non-cardinal, outward, non-adjacent) — the drawn graph's own. */
	readonly annotations: MapGraph['annotations'];
	/**
	 * D211: the map's own control, under its heading — the style chooser
	 * where the pack offers more than one style (Mike, 2026-09-25: the map
	 * type toggle belongs in the map modal, not among the settings). The
	 * shell renders it, because the choice is the store's; the panel only
	 * gives it its place.
	 */
	readonly chooser?: ReactNode;
	/** D210: a line above the figure when the drawing is not the one the player asked for — the shell's words. */
	readonly notice?: string | undefined;
	/** The engine's positioned drawing primitives, in the style the player is looking at (D209). */
	readonly layout: MapLayout;
	/**
	 * D210: draws a `scene` layout — the shell's, because the renderer is a
	 * package this one never imports. Absent while the shell has none to
	 * offer, the panel says so in one line rather than drawing nothing.
	 */
	readonly renderScene?: (layout: SceneMapLayout, title: string) => ReactNode;
	/** What the pack calls its map (D189) — the heading and the figure's name. */
	readonly title: string;
	/** How many rooms the player has walked — the panel's heading statistic. */
	readonly visitedCount: number;
}

/**
 * Render the map panel: heading, the drawn map, and the annotation list.
 *
 * @param props - {@link MapPanelComponentProps}
 * @returns The rendered panel
 */
export const MapPanelComponent = ({ annotations, chooser, layout, notice, renderScene, title, visitedCount }: MapPanelComponentProps) => (
	<section aria-labelledby="map-panel-heading" className="map-panel">
		<h2 className="map-panel__heading" id="map-panel-heading">
			{/* D191: labeled readout — this heading wraps the PACK's title, so the agreement was the engine's grammar around somebody else's word. */}
			{title} — locations walked: {visitedCount}
		</h2>
		{chooser === undefined ? undefined : <div className="map-panel__chooser">{chooser}</div>}
		{notice === undefined ? undefined : (
			<p className="map-panel__notice" role="status">
				{notice}
			</p>
		)}
		{/* The canvas exists to carry the drafting-grid backdrop in CSS, so the
		    STRING serializer (`renderMapSvg`) and this one still agree about
		    every drawn primitive — the decoration is the stylesheet's, not a
		    second layout. */}
		<div className="map-panel__canvas">
			{layout.style === 'basic' ? (
				<BasicMapFigure layout={layout} title={title} />
			) : layout.style === 'holo' ? (
				<HoloMapFigure layout={layout} title={title} />
			) : renderScene === undefined ? (
				<p className="map-panel__scene-pending">The 3D map has no renderer here.</p>
			) : (
				renderScene(layout, title)
			)}
		</div>
		{layout.style !== 'basic' && <HoloMapLegend layout={layout} />}
		{/* D210 (the plan's §4): a canvas is a black box to a screen reader, so
		    the scene names the text map beside it — the one map every player
		    can read is a directive away, and no game is less playable for
		    choosing the scene. */}
		{layout.style === 'scene' && <p className="map-panel__text-hint">{TEXT_MAP_HINT}</p>}
		{annotations.length > 0 && (
			<ul className="map-panel__annotations">
				{annotations.map((annotation) => (
					<li
						className="map-panel__annotation"
						key={`${annotation.directionToken}-${annotation.fromRoomName}-${annotation.toLabel}`}
					>
						<span className="map-panel__annotation-direction">{annotation.directionToken}</span>
						{': '}
						{annotation.fromRoomName} → {annotation.toLabel}
					</li>
				))}
			</ul>
		)}
	</section>
);
